import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, filter, forkJoin, of, take, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ComponentCanDeactivate } from '../../../guards/unsaved-changes.guard';
import { LookupTypeCodes, LookupValue } from '../../../models/lookup.models';
import {
  OfficeDetailDto,
  OfficeResourceDto,
  ResourceDto,
} from '../../../models/resource.models';
import {
  CreateTimeOffOfficePolicyRequest,
  PRACTICE_AREA_OVERRIDES_FRAGMENT,
  TimeOffOfficePolicyDto,
  TimeOffResourceOverrideDto,
  TimeOffTypeCodes,
  UpdateTimeOffOfficePolicyRequest,
} from '../../../models/time-off.models';
import { PracticeArea } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { BreadcrumbService } from '../../../services/breadcrumb.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { LookupService } from '../../../services/lookup.service';
import { OfficeService } from '../../../services/office.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { ResourceService } from '../../../services/resource.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { TimeOffService } from '../../../services/time-off.service';
import { describeApiError } from '../../../utils/api-error.utils';
import { BreadcrumbComponent } from '../../shared/breadcrumb/breadcrumb.component';
import { CopyableTextComponent } from '../../shared/copyable-text/copyable-text.component';
import {
  PracticeAreaOverrideEditRow,
  TimeOffPoliciesDialogComponent,
  TimeOffPoliciesDialogData,
  TimeOffPoliciesDialogResult,
} from '../../shared/time-off-policies-dialog/time-off-policies-dialog.component';
import { OfficeNotificationsComponent } from '../office-notifications/office-notifications.component';
import { OfficePracticeAreaSettingsComponent } from '../office-practice-area-settings/office-practice-area-settings.component';
import {
  ResourceMembershipDataSource,
  ResourceMembershipPanelComponent,
} from '../shared/resource-membership-panel/resource-membership-panel.component';

interface PolicyRow {
  typeCode: string;
  typeName: string;
  policyId: number | null;
  typeLookupValueId: number;
  defaultDays: number;
  maxCarryoverDays: number | null;
}

/** One practice-area override policy as displayed in the card. Every active row is in force. */
export interface PracticeAreaOverrideRow {
  policyId: number;
  practiceAreaCode: string;
  /** Resolved from the practice-area list; falls back to the code when the lookup has no match. */
  practiceAreaName: string;
  /** Drives the Vacation-only filter for the edit dialog. */
  typeCode: string;
  /** Rendered in the card's Type column. */
  typeName: string;
  // Deliberately no typeLookupValueId: the save path takes the id from the Vacation lookup, which
  // is what keeps a create and an update writing the same one. A copy here would invite a future
  // reader to use it instead, defeating that.
  defaultDays: number;
  maxCarryoverDays: number | null;
}

/** A write the dialog's override edits imply, decided from what was loaded — never by probing. */
export type OverrideSaveOp =
  | { kind: 'create'; code: string; request: CreateTimeOffOfficePolicyRequest }
  | {
      kind: 'update';
      code: string;
      id: number;
      request: UpdateTimeOffOfficePolicyRequest;
    }
  | { kind: 'delete'; code: string; id: number };

/**
 * Diff the dialog's override rows against the overrides currently loaded for the office.
 *
 * A row's identity is its (office, type, practice area) triple and the API refuses to change it,
 * so an update always carries the *stored* practice area code, and adding or removing an override
 * is a create or a delete — never an update that flips the code on or off. Codes are matched
 * case-insensitively because the server canonicalises casing on write.
 */
export function computeOverrideSaveOps(
  edited: PracticeAreaOverrideEditRow[],
  current: PracticeAreaOverrideRow[],
  vacationTypeLookupValueId: number,
  officeCode: string,
): OverrideSaveOp[] {
  const byCode = new Map(
    current.map((c) => [c.practiceAreaCode.toLowerCase(), c] as const),
  );

  const ops: OverrideSaveOp[] = [];
  for (const row of edited) {
    const existing = byCode.get(row.practiceAreaCode.toLowerCase());

    if (row.enabled && !existing) {
      ops.push({
        kind: 'create',
        code: row.practiceAreaCode,
        request: {
          officeCode,
          typeLookupValueId: vacationTypeLookupValueId,
          defaultDays: row.defaultDays ?? 0,
          maxCarryoverDays: row.maxCarryoverDays,
          practiceAreaCode: row.practiceAreaCode,
        },
      });
    } else if (row.enabled && existing) {
      const changed =
        existing.defaultDays !== row.defaultDays ||
        existing.maxCarryoverDays !== row.maxCarryoverDays;
      if (changed) {
        ops.push({
          kind: 'update',
          code: existing.practiceAreaCode,
          id: existing.policyId,
          request: {
            officeCode,
            typeLookupValueId: vacationTypeLookupValueId,
            defaultDays: row.defaultDays ?? 0,
            maxCarryoverDays: row.maxCarryoverDays,
            practiceAreaCode: existing.practiceAreaCode,
          },
        });
      }
    } else if (!row.enabled && existing) {
      ops.push({
        kind: 'delete',
        code: existing.practiceAreaCode,
        id: existing.policyId,
      });
    }
  }
  return ops;
}

const HARDCODED_TYPES = [
  { code: TimeOffTypeCodes.VACATION, name: 'Vacation' },
  { code: TimeOffTypeCodes.SICK, name: 'Sick' },
];

/**
 * Shown when the Vacation lookup value is missing, which leaves practice-area overrides
 * unwritable — they are a Vacation-only concept, and every create or update needs its id.
 */
const MISSING_VACATION_TYPE_MESSAGE =
  'The Vacation time-off type is not configured, so practice area overrides cannot be added or changed.';

/**
 * Shown when a time-off type's lookup value is missing, which leaves that type's office default
 * unwritable: buildPolicyRows gives the row a typeLookupValueId of 0, and the API rejects it.
 */
function missingTypeDefaultMessage(typeName: string): string {
  return `The ${typeName} time-off type is not configured, so its office default cannot be changed.`;
}

@Component({
  selector: 'app-office-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    RouterModule,
    BreadcrumbComponent,
    ResourceMembershipPanelComponent,
    CopyableTextComponent,
    OfficeNotificationsComponent,
    OfficePracticeAreaSettingsComponent,
  ],
  templateUrl: './office-details.component.html',
  styleUrls: ['./office-details.component.scss'],
})
export class OfficeDetailsComponent implements OnInit, ComponentCanDeactivate {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private timeOffService = inject(TimeOffService);
  private resourceService = inject(ResourceService);
  private secondaryOfficeService = inject(OfficeService);
  private lookupService = inject(LookupService);
  private practiceAreaService = inject(PracticeAreaService);
  private breadcrumbService = inject(BreadcrumbService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  readonly TimeOffTypeCodes = TimeOffTypeCodes;

  officeCode: string | null = null;
  officeDescription: string = '';
  officeDetail: OfficeDetailDto | null = null;
  isLoading = false;
  error: string | null = null;

  /** Feature flag guarding the office-notifications feature — hides the config card until it's on. */
  scheduledOfficeNotificationsEnabled = false;

  /**
   * Feature flag guarding the **expected-hours column** of the Practice Area Settings card —
   * no longer the card itself, which also carries the capacity period and is not flag-gated.
   * Ships off because the flag is seeded `false` and enabled by hand per environment: whether
   * the feature is live is a per-environment decision. See "Why it ships off" in
   * `docs/Architecture/Expected-Period-Hours-Architecture.md`.
   */
  expectedPeriodHoursEnabled = false;

  dataSource = new MatTableDataSource<PolicyRow>([]);
  overridesDataSource = new MatTableDataSource<TimeOffResourceOverrideDto>([]);
  readonly overrideDisplayedColumns = [
    'resourceName',
    'typeName',
    'effectiveYear',
    'defaultDays',
    'carryoverDaysOverride',
  ];

  /** Practice-area Vacation overrides for this office, one row per (practice area, type). */
  practiceAreaOverridesDataSource =
    new MatTableDataSource<PracticeAreaOverrideRow>([]);
  readonly practiceAreaOverrideDisplayedColumns = [
    'practiceArea',
    'typeName',
    'defaultDays',
    'maxCarryoverDays',
  ];

  // Lookup data (internal, for resolving type codes to IDs)
  private timeOffTypes: LookupValue[] = [];

  /** Firm-wide practice areas, used to name override rows and to seed the edit dialog. */
  private practiceAreas: PracticeArea[] = [];

  /** True while a time-off policy save (from the Configure dialog) is in flight. */
  isSaving = false;

  /** Drives the shared resource-membership panel for this office's secondary-office members. */
  secondaryOfficeDataSource: ResourceMembershipDataSource | null = null;

  /** Whether the per-resource time-off overrides subsection is expanded. */
  timeOffOverridesExpanded = false;

  /** Whether the per-practice-area time-off overrides subsection is expanded. */
  practiceAreaOverridesExpanded = false;

  /**
   * Set when the page was opened from the policy dialog's "edit the practice area overrides"
   * link. Held until the rows load rather than acted on at init: scrolling to a section that is
   * still empty lands in the wrong place, and the section may not be rendered at all.
   */
  private pendingOverridesFragment = false;

  private readonly practiceAreaOverridesSection = viewChild<
    ElementRef<HTMLElement>
  >('practiceAreaOverridesSection');

  get canViewSecondaryOffices(): boolean {
    return this.authService.canViewSecondaryOffices();
  }

  get canManageSecondaryOffices(): boolean {
    return this.authService.canManageSecondaryOffices();
  }

  navigateToResource(employeeId: string, event: Event): void {
    event.preventDefault();
    if (!employeeId) return;
    const state = {
      breadcrumbOverride: [
        { label: 'Offices', url: '/admin/offices' },
        {
          label: this.officeDescription || this.officeCode || '',
          url: `/admin/offices/${this.officeCode}`,
        },
        { label: ':employeeId', url: '' },
      ],
    };
    this.router.navigate(['/resources', employeeId], { state });
  }

  canAccessResource(row: TimeOffResourceOverrideDto): boolean {
    if (!row.practiceAreaCodes || row.practiceAreaCodes.length === 0) {
      return true;
    }

    const userPracticeAreas = this.authService.getCurrentUserPracticeAreas();
    if (!userPracticeAreas || userPracticeAreas.length === 0) {
      return false;
    }

    const userPracticeAreaCodes = userPracticeAreas.map(
      (pa) => pa.practiceCode,
    );
    return row.practiceAreaCodes.some((code) =>
      userPracticeAreaCodes.includes(code),
    );
  }

  get canViewOfficeNotifications(): boolean {
    return this.authService.canViewOfficeNotifications();
  }

  get canViewOfficePracticeAreaSettings(): boolean {
    return this.authService.canViewOfficePracticeAreaSettings();
  }

  get canViewTimeOffPolicies(): boolean {
    return this.authService.canViewTimeOffPolicies();
  }

  get canEditTimeOffPolicies(): boolean {
    return this.authService.canManageTimeOffPolicies();
  }

  ngOnInit(): void {
    // The office-notifications card is hidden until the feature is flag-enabled (the flag also guards
    // the backend dispatch tick), so admins can't configure a notification that would never send.
    this.featureFlagService
      .isFeatureFlagEnabled('scheduled-office-notifications')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.scheduledOfficeNotificationsEnabled = enabled;
      });

    // Same shape as the notifications gate above. isFeatureFlagEnabled seeds its cache with
    // false and emits again once the HTTP call returns, so this legitimately flips false ->
    // true on first paint; the template's @if handles that. Don't collapse it to a take(1).
    this.featureFlagService
      .isFeatureFlagEnabled('expected-period-hours')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.expectedPeriodHoursEnabled = enabled;
      });

    // Read once: this is a one-shot instruction from the incoming link, not page state. Acting
    // on every emission would drag the page back to the section on any later navigation that
    // happens to keep the fragment.
    this.route.fragment.pipe(take(1)).subscribe((fragment) => {
      this.pendingOverridesFragment =
        fragment === PRACTICE_AREA_OVERRIDES_FRAGMENT;
    });

    this.route.params.subscribe((params) => {
      const code = params['officeCode'];
      if (code) {
        this.officeCode = code;
        this.authService.currentUser$
          .pipe(
            filter((user) => user !== null),
            take(1),
          )
          .subscribe(() => {
            this.loadData();
          });
      } else {
        this.error = 'No office code provided';
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.error = null;

    // Fall back to the office code before the new office's detail resolves. This is passed straight
    // down to the notifications and practice-area-settings cards, so leaving the previous office's
    // name in place makes those cards state a rule about one office while configuring another - the
    // settings card's note would name the previous office over the new office's controls.
    //
    // The code rather than an empty string: it always names the office actually being loaded, and
    // the notifications card interpolates this field with no fallback of its own, so blanking it
    // would leave sentences with a hole in them. Matches the success handler's own
    // `description || officeCode`.
    this.officeDescription = this.officeCode ?? '';

    const canViewPolicies = this.canViewTimeOffPolicies;

    if (!canViewPolicies) {
      this.timeOffTypes = [];
      this.practiceAreas = [];
      this.dataSource.data = [];
      this.overridesDataSource.data = [];
      this.practiceAreaOverridesDataSource.data = [];
    }

    const request$ = canViewPolicies
      ? forkJoin({
          officeDetail: this.resourceService.getOfficeDetail(this.officeCode!),
          policies: this.timeOffService.getOfficePoliciesByOffice(
            this.officeCode!,
          ),
          timeOffTypes: this.lookupService.getLookupValues(
            LookupTypeCodes.TIME_OFF_REQUEST_TYPE,
          ),
          overrides: this.timeOffService.getResourcePoliciesByOffice(
            this.officeCode!,
          ),
          practiceAreaOverrides:
            this.timeOffService.getOfficePracticeAreaOverrides(
              this.officeCode!,
            ),
          // Names only. PracticeAreaService rethrows on failure, and a raw call here would take
          // the whole page down with it; without names the override rows still render by code.
          practiceAreas: this.practiceAreaService.getAllPracticeAreas().pipe(
            catchError((err) => {
              console.error('Error loading practice areas:', err);
              return of([] as PracticeArea[]);
            }),
          ),
        })
      : forkJoin({
          officeDetail: this.resourceService.getOfficeDetail(this.officeCode!),
          policies: of([] as TimeOffOfficePolicyDto[]),
          timeOffTypes: of([] as LookupValue[]),
          overrides: of([] as TimeOffResourceOverrideDto[]),
          practiceAreaOverrides: of([] as TimeOffOfficePolicyDto[]),
          practiceAreas: of([] as PracticeArea[]),
        });

    request$.subscribe({
      next: ({
        officeDetail,
        policies,
        timeOffTypes,
        overrides,
        practiceAreaOverrides,
        practiceAreas,
      }) => {
        this.officeDetail = officeDetail;
        this.officeDescription = officeDetail.description || this.officeCode!;
        this.breadcrumbService.setDynamicLabel(
          ':officeCode',
          this.officeDescription,
        );

        if (canViewPolicies) {
          this.timeOffTypes = timeOffTypes.filter((t) => t.isActive);
          this.practiceAreas = practiceAreas;
          this.buildPolicyRows(policies);
          this.overridesDataSource.data = overrides;
          this.buildPracticeAreaOverrideRows(practiceAreaOverrides);
          this.revealPracticeAreaOverridesIfRequested();
        }

        this.isLoading = false;

        if (this.canViewSecondaryOffices && this.officeCode) {
          this.secondaryOfficeDataSource = this.buildSecondaryOfficeDataSource(
            this.officeCode,
          );
        }
      },
      error: (err) => {
        console.error('Error loading office details:', err);
        this.error = 'Failed to load office details. Please try again.';
        this.isLoading = false;
      },
    });
  }

  private buildPolicyRows(policies: TimeOffOfficePolicyDto[]): void {
    this.dataSource.data = HARDCODED_TYPES.map((type) => {
      const lookupValue = this.timeOffTypes.find((t) => t.code === type.code);
      // The office endpoint returns default rows only, but guard on practiceAreaCode anyway: a
      // practice-area override must never be shown — or edited — as the office default.
      const existingPolicy = lookupValue
        ? policies.find(
            (p) =>
              p.practiceAreaCode == null &&
              p.typeLookupValueId === lookupValue.id,
          )
        : null;

      return {
        typeCode: type.code,
        typeName: type.name,
        policyId: existingPolicy?.id ?? null,
        typeLookupValueId: lookupValue?.id ?? 0,
        defaultDays: existingPolicy?.defaultDays ?? 0,
        maxCarryoverDays: existingPolicy?.maxCarryoverDays ?? null,
      };
    });
  }

  /**
   * Turn the office's override DTOs into display rows, naming each by its practice area. Rows
   * without a code are skipped defensively — the overrides endpoint never returns them, but the
   * DTO type allows it.
   */
  private buildPracticeAreaOverrideRows(
    overrides: TimeOffOfficePolicyDto[],
  ): void {
    this.practiceAreaOverridesDataSource.data = overrides
      .filter((o) => o.practiceAreaCode != null)
      .map((o) => ({
        policyId: o.id,
        practiceAreaCode: o.practiceAreaCode!,
        practiceAreaName: this.practiceAreaNameFor(o.practiceAreaCode!),
        typeCode: o.typeCode,
        typeName: o.typeName,
        defaultDays: o.defaultDays,
        maxCarryoverDays: o.maxCarryoverDays,
      }));
  }

  /**
   * Honour the "edit the practice area overrides" link from the resource policy dialog: open the
   * section and bring it into view. Runs once the rows are built, so the scroll targets the real
   * height rather than an empty section.
   *
   * Consumed on the first load and then cleared — a later refresh (a save, a retry) must not yank
   * the page back to this section while the admin is working somewhere else on it.
   */
  private revealPracticeAreaOverridesIfRequested(): void {
    if (!this.pendingOverridesFragment) return;
    this.pendingOverridesFragment = false;

    this.practiceAreaOverridesExpanded = true;

    // The section only renders inside the permission-gated card, and its body only after the
    // expand above takes effect, so the scroll waits for the next render rather than running now.
    afterNextRender(
      () => {
        this.practiceAreaOverridesSection()?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      },
      { injector: this.injector },
    );
  }

  /**
   * Display name for a practice-area code. The column carries no foreign key, so a code with no
   * match in the practice-area list is possible; showing the code keeps the row identifiable.
   */
  private practiceAreaNameFor(code: string): string {
    const match = this.practiceAreas.find(
      (pa) => pa.practiceCode.toLowerCase() === code.toLowerCase(),
    );
    return match?.description || code;
  }

  // --- Configure time-off policies ---

  /**
   * Open the Configure dialog to edit every time-off type's allotments at once,
   * then persist only the rows whose values actually changed.
   */
  openTimeOffPoliciesDialog(): void {
    if (!this.officeCode || !this.canEditTimeOffPolicies) {
      return;
    }

    const dialogRef = this.dialog.open(TimeOffPoliciesDialogComponent, {
      // Must agree with the width in the dialog's own SCSS, which out-specifies the shared
      // 540px `.entity-lookup-dialog` rule so the override table has room.
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      // A stray click on the backdrop must not discard a form this long. The dialog routes
      // ESC back to Cancel itself, so the keyboard exit still works.
      disableClose: true,
      // Don't return focus to the trigger button on close — otherwise its focus
      // overlay lingers and the button looks stuck in a pressed state.
      restoreFocus: false,
      data: {
        officeName: this.officeDescription || this.officeCode,
        policies: this.dataSource.data.map((r) => ({
          typeCode: r.typeCode,
          typeName: r.typeName,
          defaultDays: r.defaultDays,
          maxCarryoverDays: r.maxCarryoverDays,
        })),
        practiceAreaOverrides: this.buildOverrideEditRows(),
      } satisfies TimeOffPoliciesDialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result?: TimeOffPoliciesDialogResult) => {
        if (result) {
          this.saveTimeOffPolicies(result);
        }
      });
  }

  /**
   * The Vacation override rows currently loaded — the only type the dialog can override.
   *
   * The API refuses to create or update a practice-area override of any other type, so a
   * non-Vacation row can only reach the card by a direct database edit. It is shown there because
   * the server would still apply it to balances, but it is deliberately kept out of the dialog: the
   * dialog's update path writes the Vacation lookup id, which the API would reject as a type change.
   * This differs from the orphan-code case handled in buildOverrideEditRows, which arises through
   * the UI whenever a practice area is deactivated after its override was written.
   */
  private get currentVacationOverrides(): PracticeAreaOverrideRow[] {
    return this.practiceAreaOverridesDataSource.data.filter(
      (r) => r.typeCode === TimeOffTypeCodes.VACATION,
    );
  }

  /**
   * One editable row per firm practice area, enabled and prefilled where a Vacation override
   * exists. Any override whose code is not a known practice area is appended, named by its code,
   * so it can still be switched off — otherwise a stale row would be unreachable from the UI.
   */
  private buildOverrideEditRows(): PracticeAreaOverrideEditRow[] {
    const overridesByCode = new Map(
      this.currentVacationOverrides.map(
        (o) => [o.practiceAreaCode.toLowerCase(), o] as const,
      ),
    );

    const rows: PracticeAreaOverrideEditRow[] = this.practiceAreas.map((pa) => {
      const existing = overridesByCode.get(pa.practiceCode.toLowerCase());
      return {
        practiceAreaCode: existing?.practiceAreaCode ?? pa.practiceCode,
        practiceAreaName: pa.description || pa.practiceCode,
        policyId: existing?.policyId ?? null,
        enabled: !!existing,
        defaultDays: existing?.defaultDays ?? null,
        maxCarryoverDays: existing?.maxCarryoverDays ?? null,
      };
    });

    const knownCodes = new Set(
      this.practiceAreas.map((pa) => pa.practiceCode.toLowerCase()),
    );
    for (const orphan of this.currentVacationOverrides) {
      if (!knownCodes.has(orphan.practiceAreaCode.toLowerCase())) {
        rows.push({
          practiceAreaCode: orphan.practiceAreaCode,
          practiceAreaName: orphan.practiceAreaName,
          policyId: orphan.policyId,
          enabled: true,
          defaultDays: orphan.defaultDays,
          maxCarryoverDays: orphan.maxCarryoverDays,
        });
      }
    }

    return rows;
  }

  /**
   * Persist what changed in the Configure dialog: create-or-update each changed office default,
   * and create, update or delete practice-area overrides per the diff. Office-default requests
   * deliberately carry no practiceAreaCode key; override requests always carry the stored one.
   */
  private saveTimeOffPolicies(result: TimeOffPoliciesDialogResult): void {
    if (!this.officeCode) {
      return;
    }
    const officeCode = this.officeCode;

    const rows = this.dataSource.data;
    const rowFor = (typeCode: string) =>
      rows.find((r) => r.typeCode === typeCode);
    const edited = result.policies.filter((e) => {
      const current = rowFor(e.typeCode);
      return (
        current &&
        (current.defaultDays !== e.defaultDays ||
          current.maxCarryoverDays !== e.maxCarryoverDays)
      );
    });

    // Every write below goes into one forkJoin, which fails fast, so a single request the API is
    // certain to reject would take the whole batch down with it. Anything unwritable is dropped
    // here instead and reported after the rest has saved.
    const dropped: string[] = [];

    // An office default whose type lookup is missing carries typeLookupValueId 0 (see
    // buildPolicyRows), which the API rejects as an invalid type.
    const changed = edited.filter((e) => {
      const row = rowFor(e.typeCode)!;
      if (row.typeLookupValueId) return true;
      dropped.push(missingTypeDefaultMessage(row.typeName));
      return false;
    });

    const vacationTypeId = this.timeOffTypes.find(
      (t) => t.code === TimeOffTypeCodes.VACATION,
    )?.id;
    let overrideOps = computeOverrideSaveOps(
      result.practiceAreaOverrides,
      this.currentVacationOverrides,
      vacationTypeId ?? 0,
      officeCode,
    );

    // Only create and update carry the Vacation lookup id in their body; a delete addresses an
    // existing row by id and needs nothing. So when the lookup is missing, drop just those two
    // kinds — removing a stale override still works.
    if (!vacationTypeId && overrideOps.some((op) => op.kind !== 'delete')) {
      overrideOps = overrideOps.filter((op) => op.kind === 'delete');
      dropped.push(MISSING_VACATION_TYPE_MESSAGE);
    }

    if (changed.length === 0 && overrideOps.length === 0) {
      // Nothing left to write, so this is the only chance to explain the drops.
      if (dropped.length > 0) {
        this.snackbarService.showError(dropped.join(' '));
      }
      return;
    }

    this.isSaving = true;

    const defaultSaves = changed.map((edit) => {
      const row = rows.find((r) => r.typeCode === edit.typeCode)!;
      const request = {
        officeCode,
        typeLookupValueId: row.typeLookupValueId,
        defaultDays: edit.defaultDays,
        maxCarryoverDays: edit.maxCarryoverDays,
      };
      const save$ = row.policyId
        ? this.timeOffService.updateOfficePolicy(row.policyId, request)
        : this.timeOffService.createOfficePolicy(request);
      return save$.pipe(map((saved) => ({ typeCode: edit.typeCode, saved })));
    });

    const overrideSaves = overrideOps.map((op): Observable<unknown> => {
      switch (op.kind) {
        case 'create':
          return this.timeOffService.createOfficePolicy(op.request);
        case 'update':
          return this.timeOffService.updateOfficePolicy(op.id, op.request);
        case 'delete':
          return this.timeOffService.deleteOfficePolicy(op.id);
      }
    });

    // forkJoin([]) completes without emitting, so each empty group is replaced by of([]).
    forkJoin({
      defaults: defaultSaves.length > 0 ? forkJoin(defaultSaves) : of([]),
      overrides: overrideSaves.length > 0 ? forkJoin(overrideSaves) : of([]),
    }).subscribe({
      next: ({ defaults }) => {
        this.dataSource.data = this.dataSource.data.map((r) => {
          const match = defaults.find((res) => res.typeCode === r.typeCode);
          return match
            ? {
                ...r,
                policyId: match.saved.id,
                defaultDays: match.saved.defaultDays,
                maxCarryoverDays: match.saved.maxCarryoverDays,
              }
            : r;
        });

        if (overrideOps.length === 0) {
          this.isSaving = false;
          this.reportSaveOutcome(dropped);
          return;
        }

        // Re-read rather than patch: deletes return no body, and the server canonicalises the
        // practice-area code's casing, so only the server knows what the card should now show.
        this.refreshPracticeAreaOverrides().subscribe({
          next: () => {
            this.isSaving = false;
            this.reportSaveOutcome(dropped);
          },
          error: () => {
            this.isSaving = false;
            this.snackbarService.showError(
              'Time-off policies were saved, but the practice area overrides could not be refreshed. Reload the page to see them.',
            );
          },
        });
      },
      error: (err) => {
        console.error('Error saving time-off policies:', err);
        this.isSaving = false;
        // The API's sentence wins over our own copy, so "Practice area code 'X' was not
        // found." reaches the admin verbatim.
        this.snackbarService.showError(
          describeApiError(err, 'Failed to save time-off policies'),
        );
        // forkJoin fails fast, so some writes may have landed before the failure. Re-read both
        // lists so the card reflects the server rather than the half-applied edit.
        this.reloadPolicies();
      },
    });
  }

  /**
   * Report the save. A drop is reported as an error rather than appended to the success text:
   * the write that mattered to the admin did not happen, and a success snackbar would replace
   * a separate error one anyway, hiding it.
   */
  private reportSaveOutcome(dropped: string[]): void {
    if (dropped.length > 0) {
      this.snackbarService.showError(
        'Your other changes were saved. ' + dropped.join(' '),
      );
      return;
    }
    this.snackbarService.showSuccess('Time-off policies updated successfully');
  }

  /** Re-fetch the practice-area overrides and rebuild the card rows. */
  private refreshPracticeAreaOverrides(): Observable<void> {
    return this.timeOffService
      .getOfficePracticeAreaOverrides(this.officeCode!)
      .pipe(
        tap((overrides) => this.buildPracticeAreaOverrideRows(overrides)),
        map(() => undefined),
      );
  }

  /** Re-fetch the office defaults and the practice-area overrides after a partial failure. */
  private reloadPolicies(): void {
    forkJoin({
      policies: this.timeOffService.getOfficePoliciesByOffice(this.officeCode!),
      practiceAreaOverrides: this.timeOffService.getOfficePracticeAreaOverrides(
        this.officeCode!,
      ),
    }).subscribe({
      next: ({ policies, practiceAreaOverrides }) => {
        this.buildPolicyRows(policies);
        this.buildPracticeAreaOverrideRows(practiceAreaOverrides);
      },
      error: (err) => {
        console.error('Error reloading time-off policies:', err);
        // This reload runs because a save already failed part-way, so it is likely to fail for
        // the same reason. Left silent, the card would keep rows — and policy ids — for
        // overrides the server has already deleted, and the next save would diff against them
        // and write to ids that no longer exist. Say so rather than only logging it.
        this.snackbarService.showError(
          'The time-off policies shown may be out of date. Reload the page before editing them again.',
        );
      },
    });
  }

  canDeactivate(): boolean {
    // Editing now happens in a modal dialog with its own state, so the page
    // itself never holds unsaved changes.
    return true;
  }

  retry(): void {
    this.loadData();
  }

  // ===== Secondary office assignments =====

  /**
   * Build the data source that drives the shared resource-membership panel. Resolves both
   * primary-office members and secondary associations server-side; rows arrive as
   * OfficeResourceDtos carrying their membership for this office, which drives whether a row is
   * removable (secondary) or locked (primary) and supplies the id used to remove it.
   */
  private buildSecondaryOfficeDataSource(
    officeCode: string,
  ): ResourceMembershipDataSource {
    // The office-resources endpoint returns OfficeResourceDto rows; the shared panel hands callbacks
    // the base ResourceDto, so narrow to read this office's membership.
    const membershipFor = (resource: ResourceDto) =>
      (resource as OfficeResourceDto).officeMemberships?.find(
        (m) => m.officeCode.toLowerCase() === officeCode.toLowerCase(),
      );
    return {
      canManage: this.canManageSecondaryOffices,
      lockedTooltip:
        "This resource's primary office is sourced from the STB employee record which is controlled outside of this application, so it cannot be removed.",
      addErrorMessage: 'Failed to add secondary office',
      removeErrorMessage: 'Failed to remove secondary office',
      loadAssigned: (page, pageSize, search) =>
        this.secondaryOfficeService.getAssignedResources(
          officeCode,
          page,
          pageSize,
          search,
        ),
      searchCandidates: (page, pageSize, search) =>
        this.secondaryOfficeService.searchResources({
          page,
          pageSize,
          search,
          excludeOfficeCode: officeCode,
        }),
      add: (resource) => {
        // Stamp the optimistic row with a (removable) secondary membership for this office so it
        // renders correctly while the create call is in flight; fill in the real association id
        // when the server responds. The panel's reload then replaces it with the server row.
        const row = resource as OfficeResourceDto;
        const officeName = this.officeDescription || officeCode;
        row.officeMemberships = [
          {
            officeCode,
            officeName,
            isPrimary: false,
            secondaryAssociationId: null,
          },
        ];
        return this.secondaryOfficeService
          .create(resource.employeeId, officeCode)
          .pipe(
            tap((created) => {
              row.officeMemberships = [
                {
                  officeCode,
                  officeName,
                  isPrimary: false,
                  secondaryAssociationId: created.id,
                },
              ];
            }),
          );
      },
      remove: (resource) => {
        const membership = membershipFor(resource);
        return membership?.secondaryAssociationId == null
          ? throwError(
              () => new Error('Missing secondary office association id'),
            )
          : this.secondaryOfficeService.delete(
              membership.secondaryAssociationId,
            );
      },
      isRemovable: (resource) => {
        const membership = membershipFor(resource);
        return !!membership && !membership.isPrimary;
      },
    };
  }
}
